import type { AxiosError } from "axios";
import { client } from "./client";
import type { ConflictResponse, ILnp } from "../type";

export type UpdateLnpPayload = Partial<ILnp> & { version: number };

export interface PaginatedLnp {
  count: number;
  next: string | null;
  previous: string | null;
  results: ILnp[];
}

/**
 * Fetch a single page of Lnp rows for the editable table.
 * Reuses the existing TextSearch endpoint so server-side filtering / search
 * stays consistent with the read-only browse experience.
 */
export async function fetchLnpPage(params: {
  page?: number;
  pageSize?: number;
  search?: string;
  searchField?: string;
  ordering?: string;
  status?: string;
}): Promise<PaginatedLnp> {
  const resp = await client.get<PaginatedLnp>("/search/admin/lnp/", {
    params: {
      page: params.page ?? 1,
      page_size: params.pageSize ?? 20,
      ...(params.search ? { search: params.search } : {}),
      ...(params.searchField ? { search_field: params.searchField } : {}),
      ...(params.ordering ? { ordering: params.ordering } : {}),
      ...(params.status ? { status: params.status } : {}),
    },
  });
  return resp.data;
}

/**
 * Send a partial update for a single Lnp row.
 * Throws on any non-2xx — callers must handle 409 (conflict) explicitly via
 * `extractConflict` to drive the ConflictModal.
 */
export async function updateLnp(
  pk: number,
  payload: UpdateLnpPayload
): Promise<ILnp> {
  const resp = await client.patch<ILnp>(`/search/admin/lnp/${pk}/`, payload);
  return resp.data;
}

/**
 * If the AxiosError is a 409 conflict from the edit endpoint, return the
 * server's authoritative `current` row. Otherwise return null.
 */
export function extractConflict(err: unknown): ILnp | null {
  const axErr = err as AxiosError<ConflictResponse>;
  if (axErr.response?.status === 409 && axErr.response.data?.current) {
    return axErr.response.data.current as ILnp;
  }
  return null;
}

/**
 * Delete a single Lnp row.
 */
export async function deleteLnp(pk: number): Promise<void> {
  await client.delete(`/search/admin/lnp/${pk}/`);
}

/**
 * Bulk delete Lnp rows.
 */
export async function bulkDeleteLnp(pks: number[]): Promise<void> {
  await client.post('/search/admin/lnp/bulk_delete/', { pks });
}

/**
 * Bulk update Lnp rows directly.
 */
export async function bulkUpdateLnp(updates: Record<string, Partial<ILnp>>): Promise<void> {
  await client.post('/search/admin/lnp/bulk_update/', { updates });
}

/**
 * Submit modified rows to create an ImportJob task.
 */
export async function createImportJobFromData(payload: {
  model_name: string;
  filename: string;
  rows: Record<string, unknown>[];
  conflict_resolution: string;
  auto_confirm?: boolean;
}): Promise<any> {
  const resp = await client.post('/search/admin/import/create-from-data/', payload);
  return resp.data;
}
